import React, { useState, useEffect } from 'react';
import { OrderDetailsDTO, CreateReceiptRequest } from '../../types/order';
import { API_BASE } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

interface CreateReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: OrderDetailsDTO;
  onReceiptCreated: () => Promise<void>; // Function to refetch receipts
}

const CreateReceiptModal: React.FC<CreateReceiptModalProps> = ({
  isOpen,
  onClose,
  order,
  onReceiptCreated,
}) => {
  const { token } = useAuth();
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [transactionId, setTransactionId] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [gstPercentage, setGstPercentage] = useState('18');
  const [isInterstate, setIsInterstate] = useState(false);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate remaining balance
  const invoiceTotal = order.price?.total || 0;
  const totalPaid = (order.receipts || []).reduce((sum, r) => sum + r.amount, 0);
  const remainingBalance = Math.max(0, invoiceTotal - totalPaid);
  const newRemainingBalance = remainingBalance - (parseFloat(amount) || 0);

  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setPaymentMethod('cash');
      setTransactionId('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setGstPercentage('18');
      setIsInterstate(false);
      setNotes('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('Authentication required');
      }

      const amountNum = parseFloat(amount);
      if (!amountNum || amountNum <= 0) {
        throw new Error('Payment amount must be greater than 0');
      }

      if (amountNum > remainingBalance) {
        throw new Error(`Payment amount (₹${amountNum.toFixed(2)}) exceeds remaining balance (₹${remainingBalance.toFixed(2)})`);
      }

      const receiptData: CreateReceiptRequest = {
        amount: amountNum,
        payment_method: paymentMethod,
        transaction_id: transactionId || undefined,
        payment_date: paymentDate ? new Date(paymentDate).toISOString() : undefined,
        gst_percentage: gstPercentage ? parseFloat(gstPercentage) : undefined,
        is_interstate: isInterstate,
        notes: notes || undefined,
      };

      const response = await fetch(`${API_BASE}/api/orders/${order.id}/receipts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(receiptData),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to create receipt');
      }

      await onReceiptCreated();
      onClose();
    } catch (_err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Failed to create receipt:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-receipt-modal-title"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 id="create-receipt-modal-title" className="text-xl font-semibold text-gray-900">
              Create Receipt - Order #{order.id}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          {/* Balance Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Invoice Total:</span>
                <span className="font-semibold">₹{invoiceTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Amount Paid:</span>
                <span className="font-semibold">₹{totalPaid.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-blue-300">
                <span className="text-gray-700 font-medium">Remaining Balance:</span>
                <span className={`font-bold ${remainingBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ₹{remainingBalance.toFixed(2)}
                </span>
              </div>
              {amount && parseFloat(amount) > 0 && (
                <div className="flex justify-between pt-1">
                  <span className="text-gray-600 text-xs">New Remaining:</span>
                  <span className={`text-xs font-semibold ${newRemainingBalance >= 0 ? 'text-gray-700' : 'text-red-600'}`}>
                    ₹{newRemainingBalance.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                Payment Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="amount"
                step="0.01"
                min="0.01"
                max={remainingBalance}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter payment amount"
              />
              <p className="mt-1 text-xs text-gray-500">
                Maximum: ₹{remainingBalance.toFixed(2)}
              </p>
            </div>

            <div>
              <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method <span className="text-red-500">*</span>
              </label>
              <select
                id="paymentMethod"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="cash">Cash</option>
                <option value="credit_card">Credit Card</option>
                <option value="debit_card">Debit Card</option>
                <option value="upi">UPI</option>
                <option value="razorpay">Razorpay</option>
                <option value="stripe">Stripe</option>
                <option value="cheque">Cheque</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
            </div>

            {paymentMethod !== 'cash' && (
              <div>
                <label htmlFor="transactionId" className="block text-sm font-medium text-gray-700 mb-1">
                  Transaction ID
                </label>
                <input
                  type="text"
                  id="transactionId"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter transaction ID (optional)"
                />
              </div>
            )}

            <div>
              <label htmlFor="paymentDate" className="block text-sm font-medium text-gray-700 mb-1">
                Payment Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="paymentDate"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="gstPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                GST Percentage (%)
              </label>
              <input
                type="number"
                id="gstPercentage"
                step="0.01"
                min="0"
                max="100"
                value={gstPercentage}
                onChange={(e) => setGstPercentage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 18"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isInterstate"
                checked={isInterstate}
                onChange={(e) => setIsInterstate(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="isInterstate" className="text-sm text-gray-700">
                Interstate Transaction (IGST)
              </label>
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Additional notes (optional)"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !amount || parseFloat(amount) <= 0}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Receipt'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateReceiptModal;

